import { useEffect, useState } from 'react';
import { formatPhoneNumber } from '@aviv-estimation-funnel/shells/src/app/helpers/formatPhoneNumber';
import { Box } from '@gemini/core';
import { useAppContext, useAppDispatch } from '@mfe-presentation/contexts';
import { FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { Link, Modal } from '@gemini/ui';
import { EActionType } from '@mfe-types/context';
import { SmsServiceCode, ResultStatus } from '@mfe-presentation/constants/userVerification';
import useSmsService from '@mfe-presentation/hooks/useSmsService';
import PhoneNumberVerificationForm from '@mfe-presentation/components/PhoneNumberVerificationForm';
import { addError } from '@mfe-helpers/datadog';
import { ValidationResult } from '@aviv-estimation-funnel/data-validation/validation-result';
import { useTranslation } from '@aviv-unified-frontend/runtime/i18n';
import { NAME_SPACE } from '@mfe-presentation/hooks/useLoadTranslations';
import useNavigation from '@mfe-presentation/hooks/useNavigation';

const PhoneNumberVerificationModal = () => {
    const {
        formValues,
        config,
        currentStep: { name },
        userVerification,
        timer: { startTimer },
    } = useAppContext();
    const dispatch = useAppDispatch();
    const { setValue } = useFormContext();
    const { submitForm } = useNavigation();

    const { t } = useTranslation(NAME_SPACE);

    const [isValidatingPhoneNumber, setIsValidatingPhoneNumber] = useState(false);

    const formResolver: any = async (fieldValues: Record<string, any>) => {
        let errors = {};
        const validator = config.identity.getFields(formValues).phoneNumber.validators;

        const { phoneNumberRegionCode, phoneNumber } = fieldValues;

        if (validator) {
            const validationResult: ValidationResult = await validator.validate('phoneNumber', {
                phoneNumberRegionCode,
                phoneNumber,
            });

            errors = {
                phoneNumber: validationResult.errorsPerFieldName['phoneNumber']
                    ? {
                          type: validationResult.errorsPerFieldName['phoneNumber'][0].code,
                          types: validationResult.errorsPerFieldName['phoneNumber'],
                      }
                    : {},
            };
        }

        return {
            values: fieldValues,
            errors,
        };
    };

    const formProviderMethods = useForm({
        mode: 'all',
        defaultValues: {
            phoneNumberRegionCode: formValues.phoneNumberRegionCode,
            phoneNumber: '',
        },
        resolver: async (values) => formResolver(values),
    });

    const phoneNumber = useWatch({
        control: formProviderMethods.control,
        name: 'phoneNumber',
    });

    useEffect(() => {
        if (phoneNumber) {
            formProviderMethods.trigger();
        }
    }, [phoneNumber]);

    const handleSubmit = async (close: () => void) => {
        formProviderMethods.trigger();

        const { phoneNumberRegionCode, phoneNumber } = formProviderMethods.getValues();

        dispatch({
            type: EActionType.SetHasUpdatedPhoneNumber,
            payload: true,
        });

        const resolver = await formResolver({ phoneNumberRegionCode, phoneNumber });

        if (Object.keys(resolver.errors.phoneNumber).length) {
            return;
        }

        setIsValidatingPhoneNumber(true);

        try {
            dispatch({
                type: EActionType.SetUserVerification,
                payload: { ...userVerification, isFetching: true },
            });
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const response = await useSmsService(
                { ...formValues, phoneNumberRegionCode, phoneNumber },
                config,
                name,
            );

            setIsValidatingPhoneNumber(false);
            setValue('phoneNumberRegionCode', phoneNumberRegionCode);
            setValue('phoneNumber', phoneNumber);
            setValue('phoneNumberVerificationCode', '');

            dispatch({
                type: EActionType.SetUserVerification,
                payload: { ...response, isFetching: false },
            });

            if (response?.isVerificationRequired) {
                startTimer();

                dispatch({
                    type: EActionType.SetToastMessage,
                    payload: {
                        type: 'success',
                        status:
                            response.status === ResultStatus.SmsSent
                                ? SmsServiceCode.VerificationCodeSent
                                : response.status,
                        params: {
                            number: formatPhoneNumber(phoneNumber || '', phoneNumberRegionCode),
                        },
                    },
                });
            } else {
                // Otherwise, if the new phone number verification is already validated or not required or unavailbale, submit the form
                await submitForm({
                    formData: formValues,
                    userVerification: response,
                });
            }

            close();
            formProviderMethods.resetField('phoneNumberRegionCode');
            formProviderMethods.resetField('phoneNumber');
        } catch (error: any) {
            setIsValidatingPhoneNumber(false);
            close();
            addError(error);
            dispatch({
                type: EActionType.SetToastMessage,
                payload: {
                    status: SmsServiceCode.VerificationCodeServerError,
                },
            });
            dispatch({
                type: EActionType.SetHasUpdatedPhoneNumber,
                payload: false,
            });
            dispatch({
                type: EActionType.SetUserVerification,
                payload: {
                    ...userVerification,
                    isFetching: false,
                },
            });
        }
    };

    return (
        <Modal
            isDismissable
            footerStartButtonProps={(close: () => void) => ({
                children: t(`step_verification.updatePhoneNumber.modal.button.cancel`),
                onPress: () => close(),
            })}
            footerEndButtonProps={(close: () => void) => ({
                children: t(`step_verification.updatePhoneNumber.modal.button.submit`),
                onPress: async () => handleSubmit(close),
                isLoading: isValidatingPhoneNumber,
            })}
            trigger={(props) => (
                //  @ts-expect-error no href
                <Link {...props} variant="inline">
                    {t(`step_verification.updatePhoneNumber.linkText`)}
                </Link>
            )}
        >
            <Box display="flex" flexDirection="column">
                <Box marginBottom="spacing.24" typography="typography.headline.28.bold">
                    {t(`step_verification.updatePhoneNumber.modal.title`)}
                </Box>
                <FormProvider {...formProviderMethods}>
                    <PhoneNumberVerificationForm />
                </FormProvider>
            </Box>
        </Modal>
    );
};

export default PhoneNumberVerificationModal;
